import _ from 'lodash';
import { action, computed, makeObservable, observable, toJS } from 'mobx';

export type PlaylistItem = {
    /**
     * 플레이리스트 내부에서 할당하는 아이디
     * 정렬이나 삭제 등을 위해 사용
     */
    playlistItemId: string;
    videoId: string;
    title: string;
};

const PLAYLIST_KEY = 'playlist';
const MAX_COUNT = 100;

interface PlaylistSnapshot {
    items: PlaylistItem[];
    isLoop: boolean;
}

class Playlist {
    items: PlaylistItem[] = [];
    isLoop = false;
    isRestored = false;

    debounceSave: () => void;

    constructor() {
        makeObservable(this, {
            items: observable,
            isLoop: observable,

            isEmpty: computed,
            firstItem: computed,
            totalCount: computed,

            prepend: action,
            append: action,
            remove: action,
            setLoop: action,
            restore: action,
        });

        this.debounceSave = _.debounce(() => {
            this.save();
        }, 500);
    }

    get isEmpty(): boolean {
        return _.isEmpty(this.items);
    }

    get firstItem(): PlaylistItem | null {
        return _.head(this.items) || null;
    }

    get totalCount(): number {
        return this.items.length;
    }

    prepend(item: PlaylistItem): number {
        this.items.unshift(item);

        if (this.items.length > MAX_COUNT) {
            this.items.pop();
        }

        this.debounceSave();
        return 0;
    }

    append(item: PlaylistItem): number {
        this.items.push(item);

        if (this.items.length > MAX_COUNT) {
            this.items.shift();
        }

        this.debounceSave();
        return this.items.length - 1;
    }

    remove(item: PlaylistItem): void {
        _.remove(this.items, { playlistItemId: item.playlistItemId });
        this.debounceSave();
    }

    setLoop(isLoop: boolean): void {
        this.isLoop = isLoop;
        this.debounceSave();
    }

    /**
     * 현재 플레이리스트의 상태를 로컬 스토리지에 저장한다
     */
    save(): void {
        if (!this.isRestored) {
            return;
        }

        const snapshot: PlaylistSnapshot = {
            items: toJS(this.items),
            isLoop: this.isLoop,
        };
        localStorage.setItem(PLAYLIST_KEY, JSON.stringify(snapshot));
    }

    /**
     * 로컬 스토리지에서 플레이리스트를 복원한다
     * 생성된 이후 최초 1회만 실행된다
     */
    restore(): void {
        if (this.isRestored) {
            return;
        }

        const snapshot = localStorage.getItem(PLAYLIST_KEY);

        try {
            const restored: PlaylistSnapshot = JSON.parse(snapshot || '{}');
            if (!_.isEmpty(restored)) {
                this.items.length = 0;
                _.forEach(restored.items, (item) => {
                    this.items.push(item);
                });

                this.isLoop = restored.isLoop;
            }
        } catch (e) {
            // ignore
        }

        this.isRestored = true;
    }

    // getters
    getByIndex(index: number): PlaylistItem | null {
        return this.items[index] || null;
    }

    getByPlaylistItemId(playlistItemId: string): PlaylistItem | null {
        return _.find(this.items, { playlistItemId }) || null;
    }

    getItemIndex(playlistItemId: string): number {
        return _.findIndex(this.items, { playlistItemId });
    }

    getNextIndex(index: number): number {
        let nextIndex = index + 1;

        if (nextIndex >= this.items.length) {
            if (this.isLoop) {
                nextIndex = 0;
            } else {
                nextIndex = -1;
            }
        }

        return nextIndex;
    }

    getPrevIndex(index: number): number {
        let prevIndex = index - 1;

        if (prevIndex < 0) {
            if (this.isLoop) {
                prevIndex = this.items.length - 1;
            } else {
                prevIndex = -1;
            }
        }

        return prevIndex;
    }
}

export default Playlist;
